import { ITEM_TYPE } from '@/utils/enum';

const DEFAULT_ATTACH_LEVEL = 1;
const MAX_ATTACH_LEVEL = 2;

/**
 * Resolve attachLevel from row (defaults to 1 for legacy L1-only rows).
 */
export function resolveAttachLevel(row) {
  const level = Number(row?.attachLevel);
  if (level === MAX_ATTACH_LEVEL) {
    return MAX_ATTACH_LEVEL;
  }
  return DEFAULT_ATTACH_LEVEL;
}

/**
 * Filter L1 attach rows under a parent line id.
 */
export function filterL1AttachRows(flatAttachRows, parentId, parentField = 'belongBillDetailId') {
  return flatAttachRows.filter(
    (row) =>
      row[parentField] === parentId && resolveAttachLevel(row) === DEFAULT_ATTACH_LEVEL,
  );
}

/**
 * Filter L2 attach rows under an L1 line id.
 */
export function filterL2AttachRows(flatAttachRows, l1Id, parentField = 'belongBillDetailId') {
  return flatAttachRows.filter(
    (row) => row[parentField] === l1Id && resolveAttachLevel(row) === MAX_ATTACH_LEVEL,
  );
}

/**
 * Flat attach rows → nested payload: L1[] each with attachs[] (L2 only).
 * @param {Array} flatAttachRows - all attach lines (L1 + L2)
 * @param {string|number} mainParentId - client id of MAIN line
 * @param {Function} mapRow - maps a flat row to payload shape
 * @param {string} parentField - belongBillDetailId | belongOrderDetailId
 */
export function nestAttachForPayload(
  flatAttachRows,
  mainParentId,
  mapRow,
  parentField = 'belongBillDetailId',
) {
  const l1Rows = filterL1AttachRows(flatAttachRows, mainParentId, parentField);
  return l1Rows.map((l1Row) => {
    const l2Rows = filterL2AttachRows(flatAttachRows, l1Row.id, parentField);
    const mapped = mapRow(l1Row);
    mapped.attachLevel = DEFAULT_ATTACH_LEVEL;
    mapped.attachs = l2Rows.map((l2Row) => {
      const mappedL2 = mapRow(l2Row);
      mappedL2.attachLevel = MAX_ATTACH_LEVEL;
      return mappedL2;
    });
    return mapped;
  });
}

/**
 * Collect all descendant attach line ids for cascade delete (L1 → L2).
 */
export function collectAttachDescendantIds(
  parentId,
  items,
  parentField = 'belongBillDetailId',
  idField = 'id',
) {
  const direct = items.filter((item) => item[parentField] === parentId);
  return direct.flatMap((child) => [
    child[idField],
    ...collectAttachDescendantIds(child[idField], items, parentField, idField),
  ]);
}

/**
 * Count rows to remove including parent (1 + descendants).
 */
export function countAttachCascadeRemove(
  parentId,
  items,
  parentField = 'belongBillDetailId',
  idField = 'id',
) {
  return 1 + collectAttachDescendantIds(parentId, items, parentField, idField).length;
}

/**
 * Rebuild flat display order: MAIN → L1 → L2 for each main.
 * @param {Array} mainItems - MAIN rows (already ordered)
 * @param {Array} childItems - non-MAIN rows (gifts + attach)
 * @param {Object} options
 */
export function rebuildAttachTreeOrder(
  mainItems,
  childItems,
  {
    mainIdField = 'id',
    parentField = 'belongBillDetailId',
    attachItemType = ITEM_TYPE.ATTACH,
    itemTypeField = 'itemType',
    orderItemTypeField = 'orderItemType',
  } = {},
) {
  const sortedList = [];

  mainItems.forEach((mainItem, index) => {
    sortedList.push({ ...mainItem, stt: index + 1 });

    const isAttach = (item) => {
      const type = item[itemTypeField] ?? item[orderItemTypeField];
      return type === attachItemType;
    };

    const attachPool = childItems.filter(isAttach);
    const giftPool = childItems.filter((item) => !isAttach(item));

    const giftsUnderMain = giftPool.filter(
      (item) => item[parentField] === mainItem[mainIdField],
    );
    sortedList.push(...giftsUnderMain);

    const l1Rows = filterL1AttachRows(attachPool, mainItem[mainIdField], parentField);
    l1Rows.forEach((l1Row) => {
      sortedList.push(l1Row);
      const l2Rows = filterL2AttachRows(attachPool, l1Row.id, parentField);
      sortedList.push(...l2Rows);
    });
  });

  return sortedList;
}

/**
 * Find MAIN productId for an attach row by walking belong chain.
 */
export function findMainProductIdForAttachRow(row, allItems, parentField = 'belongBillDetailId') {
  let current = row;
  const visited = new Set();
  while (current && current[parentField]) {
    if (visited.has(current[parentField])) {
      break;
    }
    visited.add(current[parentField]);
    const parent = allItems.find((item) => item.id === current[parentField]);
    if (!parent) {
      break;
    }
    if (
      parent.itemType === ITEM_TYPE.MAIN ||
      parent.orderItemType === ITEM_TYPE.MAIN
    ) {
      return parent.productId;
    }
    current = parent;
  }
  return null;
}
